/**
 * @fileoverview Sales Pipeline backlog query hook.
 * @description Wraps GET /sales/prospects (every page) in a TanStack query, keyed by the table's
 * filters, so each filter change reads its own cached result.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useQuery } from '@tanstack/react-query'

import { SALES_CRM_QUERY_KEYS } from '@/features/sales/constants/prospects-query-keys.constants'
import { getAllProspects } from '@/features/sales/services/prospects.service'

import type { ProspectListFilters } from '@/features/sales/types/prospect.types'

/**
 * @description Fetches the backlog matching the filters.
 * @param {ProspectListFilters} filters - Search, filters and order.
 * @returns {ReturnType<typeof useQuery>} The query (data is every matching prospect).
 */
export function useProspects(filters: ProspectListFilters) {
  return useQuery({
    queryKey: SALES_CRM_QUERY_KEYS.prospects(filters),
    queryFn: () => getAllProspects(filters),
    placeholderData: (previous) => previous,
  })
}
